<template>
  <v-card outlined class="mb-4 stock-product-movement-filters">
    <v-card-subtitle class="pb-0 font-weight-medium">
      {{ $t("STOCK.PRODUCT_MOVEMENTS.FILTER_TITLE") }}
    </v-card-subtitle>
    <v-card-text>
      <v-row dense>
        <v-col cols="12" md="4">
          <StockProductPicker
            ref="productPicker"
            v-model="selectedProductId"
            :label="$t('STOCK.PRODUCTS.PRODUCT')"
            :show-category-filter="false"
          />
        </v-col>
        <v-col cols="12" md="3">
          <v-combobox
            v-model="lotNumber"
            :items="lotOptions"
            item-text="label"
            item-value="lot_number"
            :label="$t('STOCK.RECEIPTS.LOT_NUMBER')"
            :loading="isLoadingLots"
            cache-items
            hide-no-data
            dense
            outlined
            hide-details
            clearable
            prepend-inner-icon="mdi-barcode"
            @update:search-input="onLotSearchInput"
            @keyup.enter="applyFilters"
          ></v-combobox>
        </v-col>
        <v-col cols="12" md="3">
          <v-text-field
            v-model="search"
            :label="$t('GENERAL.LABELS.SEARCH')"
            dense
            outlined
            hide-details
            clearable
            prepend-inner-icon="mdi-magnify"
            @keyup.enter="applyFilters"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="2">
          <v-autocomplete
            v-model="filters.stock_product_department_id"
            :items="departmentOptions"
            :loading="isLoadingOptions"
            item-text="label"
            item-value="id"
            :label="$t('STOCK.PRODUCTS.DEPARTMENT')"
            dense
            outlined
            hide-details
            clearable
            multiple
            small-chips
            deletable-chips
            @change="handleDepartmentChange"
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" md="2">
          <v-autocomplete
            v-model="filters.stock_product_category_id"
            :items="categoryOptions"
            :loading="isLoadingOptions"
            item-text="label"
            item-value="id"
            :label="$t('STOCK.PRODUCTS.CATEGORY')"
            dense
            outlined
            hide-details
            clearable
            multiple
            small-chips
            deletable-chips
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" md="2">
          <v-autocomplete
            v-model="filters.temperature_id"
            :items="temperatureOptions"
            :loading="isLoadingOptions"
            item-text="label"
            item-value="id"
            :label="$t('STOCK.PRODUCTS.TEMPERATURE')"
            dense
            outlined
            hide-details
            clearable
            multiple
            small-chips
            deletable-chips
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" md="2">
          <v-autocomplete
            v-model="filters.packaging_id"
            :items="packagingOptions"
            :loading="isLoadingOptions"
            item-text="label"
            item-value="id"
            :label="$t('STOCK.PRODUCTS.PACKAGING')"
            dense
            outlined
            hide-details
            clearable
            multiple
            small-chips
            deletable-chips
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" md="3">
          <v-autocomplete
            v-model="filters.stock_tag_ids"
            :items="tagOptions"
            :loading="isLoadingTags"
            item-text="label"
            item-value="id"
            :label="$t('STOCK.PRODUCTS.TAGS')"
            dense
            outlined
            hide-details
            clearable
            multiple
            small-chips
            deletable-chips
            :search-input.sync="tagSearch"
            @update:search-input="loadTags"
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" class="d-flex align-center flex-wrap">
          <v-btn color="primary" small :loading="isApplying" @click="applyFilters">
            {{ $t("STOCK.PRODUCT_FINDER.SEARCH") }}
          </v-btn>
          <v-btn text small class="ml-2" :disabled="isApplying" @click="clearFilters">
            {{ $t("STOCK.PRODUCT_FINDER.CLEAR") }}
          </v-btn>
        </v-col>
      </v-row>

      <v-alert type="info" dense text class="mb-0 mt-2">
        {{ $t("STOCK.PRODUCT_MOVEMENTS.FILTER_HINT") }}
      </v-alert>
    </v-card-text>
  </v-card>
</template>

<script>
import { debounce } from "lodash";
import StockProductPicker from "@/views/partials/stock/StockProductPicker.vue";
import {
  GET_STOCK_TEMPERATURE_OPTIONS,
  GET_STOCK_PACKAGING_OPTIONS,
  GET_STOCK_TAG_OPTIONS,
  GET_STOCK_LOT_OPTIONS,
} from "@/store/stock/stockProducts.module";
import { GET_STOCK_PRODUCT_CATEGORY_OPTIONS } from "@/store/stock/stockProductCategories.module";
import { GET_STOCK_PRODUCT_DEPARTMENT_OPTIONS } from "@/store/stock/stockProductDepartments.module";

const emptyFilters = () => ({
  stock_product_department_id: [],
  stock_product_category_id: [],
  temperature_id: [],
  packaging_id: [],
  stock_tag_ids: [],
});

export default {
  name: "StockProductMovementFilters",
  components: { StockProductPicker },
  data() {
    return {
      search: "",
      selectedProductId: null,
      lotNumber: "",
      lotOptions: [],
      suppressLotSearch: false,
      filters: emptyFilters(),
      isApplying: false,
      isLoadingOptions: false,
      isLoadingTags: false,
      isLoadingLots: false,
      departmentOptions: [],
      allCategories: [],
      temperatureOptions: [],
      packagingOptions: [],
      tagOptions: [],
      tagSearch: "",
    };
  },
  computed: {
    categoryOptions() {
      const departmentIds = this.filters.stock_product_department_id || [];
      if (!departmentIds.length) {
        return this.allCategories;
      }
      return this.allCategories.filter((category) =>
        departmentIds.includes(category.stock_product_department_id)
      );
    },
  },
  watch: {
    selectedProductId(productId, previousId) {
      if (this.suppressLotSearch || productId === previousId) {
        return;
      }
      this.loadLots("");
    },
  },
  mounted() {
    this.loadFilterOptions();
    this.loadTags("");
    this.loadLots("");
  },
  methods: {
    lotNumberValue() {
      if (this.lotNumber && typeof this.lotNumber === "object") {
        return (this.lotNumber.lot_number || this.lotNumber.label || "").trim();
      }
      return String(this.lotNumber || "").trim();
    },
    handleDepartmentChange() {
      const allowedIds = this.categoryOptions.map((category) => category.id);
      this.filters.stock_product_category_id = this.filters.stock_product_category_id.filter(
        (id) => allowedIds.includes(id)
      );
    },
    onLotSearchInput(value) {
      if (this.suppressLotSearch || value == null) {
        return;
      }
      if (String(value) === this.lotNumberValue()) {
        return;
      }
      this.loadLots(value);
    },
    buildApiFilters() {
      const apiFilters = {};

      if (this.selectedProductId) {
        apiFilters.stock_product_id = this.selectedProductId;
      }
      const lotNumber = this.lotNumberValue();
      if (lotNumber) {
        apiFilters.lot_number = lotNumber;
      }
      if ((this.search || "").trim().length >= 2) {
        apiFilters.stock_product_search = this.search.trim();
      }
      if (this.filters.stock_product_department_id.length) {
        apiFilters.stock_product_department_id = [...this.filters.stock_product_department_id];
      }
      if (this.filters.stock_product_category_id.length) {
        apiFilters.stock_product_category_id = [...this.filters.stock_product_category_id];
      }
      if (this.filters.temperature_id.length) {
        apiFilters.temperature_id = [...this.filters.temperature_id];
      }
      if (this.filters.packaging_id.length) {
        apiFilters.packaging_id = [...this.filters.packaging_id];
      }
      if (this.filters.stock_tag_ids.length) {
        apiFilters.stock_tag_ids = [...this.filters.stock_tag_ids];
      }

      return apiFilters;
    },
    applyFilters(options = {}) {
      this.isApplying = true;
      this.$emit("apply", this.buildApiFilters(), options);
      this.$nextTick(() => {
        this.isApplying = false;
      });
    },
    applyTrace(payload = {}) {
      this.suppressLotSearch = true;
      if (payload.product && this.$refs.productPicker) {
        this.$refs.productPicker.ensureOption(payload.product);
      }
      this.selectedProductId = payload.stock_product_id || null;
      this.lotNumber = payload.lot_number || "";
      this.applyFilters({ clearDates: true });
      this.$nextTick(() => {
        this.$nextTick(() => {
          this.suppressLotSearch = false;
        });
      });
    },
    clearFilters() {
      this.search = "";
      this.selectedProductId = null;
      this.lotNumber = "";
      this.lotOptions = [];
      this.filters = emptyFilters();
      this.$emit("apply", {});
    },
    loadFilterOptions() {
      this.isLoadingOptions = true;
      return Promise.all([
        this.$store.dispatch(GET_STOCK_PRODUCT_DEPARTMENT_OPTIONS, {}),
        this.$store.dispatch(GET_STOCK_PRODUCT_CATEGORY_OPTIONS, {}),
        this.$store.dispatch(GET_STOCK_TEMPERATURE_OPTIONS),
        this.$store.dispatch(GET_STOCK_PACKAGING_OPTIONS),
      ])
        .then(([departmentsResponse, categoriesResponse, temperaturesResponse, packagingsResponse]) => {
          this.departmentOptions = departmentsResponse.data || [];
          this.allCategories = categoriesResponse.data || [];
          this.temperatureOptions = temperaturesResponse.data || [];
          this.packagingOptions = packagingsResponse.data || [];
        })
        .finally(() => {
          this.isLoadingOptions = false;
        });
    },
    loadTags: debounce(function (search) {
      this.isLoadingTags = true;
      return this.$store
        .dispatch(GET_STOCK_TAG_OPTIONS, { search: search || "" })
        .then((response) => {
          this.tagOptions = response.data || [];
        })
        .finally(() => {
          this.isLoadingTags = false;
        });
    }, 300),
    loadLots: debounce(function (search) {
      const query = String(search || "").trim();
      const params = {};
      if (query) {
        params.search = query;
      }
      if (this.selectedProductId) {
        params.filters = { stock_product_id: this.selectedProductId };
      } else if (query.length < 2) {
        return Promise.resolve();
      }

      this.isLoadingLots = true;
      return this.$store
        .dispatch(GET_STOCK_LOT_OPTIONS, params)
        .then((response) => {
          this.lotOptions = response.data || [];
        })
        .finally(() => {
          this.isLoadingLots = false;
        });
    }, 300),
  },
};
</script>
